import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';

/**
 * "Install this shop", across browsers that disagree about what that means.
 *
 * There is no portable way to install a web app, so this resolves to one of
 * four honest answers rather than pretending to a fifth:
 *
 * * **`prompt`** - Chrome, Edge, Samsung Internet, and Chrome on Android. They
 *   fire `beforeinstallprompt`, which is a *one-shot* handle on the browser's
 *   own install dialog. It is captured the moment it fires, because it cannot
 *   be asked for later and it is gone as soon as the page navigates.
 * * **`ios`** - Safari on iPhone and iPad. It installs, and it will not be
 *   asked to: the shopper has to use Share → Add to Home Screen. So the only
 *   useful thing to show is exactly where that is.
 * * **`manual`** - Firefox, and desktop Safari. Some can install from a menu,
 *   some cannot; none will tell us. Instructions, and no promises.
 * * **`open`** - installed already, and this is a browser tab on a computer.
 *   Chrome and Edge can say so (`getInstalledRelatedApps`), and a link in the
 *   scheme the app registered opens its window.
 * * **`installed`** - running as the installed app right now, or installed on
 *   a phone, where no browser tab can open it. Nothing to offer.
 *
 * The one thing this never does is nag. Nothing appears on its own except to
 * a shopper who scanned the merchant's printed code; everyone else finds the
 * offer in the shop's footer, where it waits until they want it.
 */

export type InstallCapability = 'prompt' | 'ios' | 'manual' | 'open' | 'installed';

/** Chrome's non-standard event. Typed here because the DOM lib has no name for it. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * The handle, held outside React.
 *
 * Chrome fires `beforeinstallprompt` as soon as it has read a usable manifest,
 * which can be long before the storefront has fetched its context and mounted
 * anything to listen with - and it does not fire again for a listener that
 * turns up late. So it is caught from the very start of the page
 * (`listenForInstallPrompt` in `main.tsx`) and kept here until the footer's
 * button asks for it. The newest one wins: when the shop's own manifest
 * replaces the platform's, Chrome's next event is the one for the shop.
 */
let pendingPrompt: BeforeInstallPromptEvent | null = null;
const promptSubscribers = new Set<() => void>();
let listening = false;

function setPendingPrompt(event: BeforeInstallPromptEvent | null) {
  pendingPrompt = event;
  for (const notify of promptSubscribers) notify();
}

/** Start catching Chrome's install handle. Safe to call more than once. */
export function listenForInstallPrompt(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this Chrome shows its own mini-infobar, and the offer appears
    // twice in two different places.
    event.preventDefault();
    setPendingPrompt(event as BeforeInstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => setPendingPrompt(null));
}

function subscribeToPrompt(notify: () => void) {
  promptSubscribers.add(notify);
  return () => {
    promptSubscribers.delete(notify);
  };
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // Two mechanisms, because iOS supports only the second: `display-mode` is
  // the standard query, and `navigator.standalone` is Safari's own flag.
  const displayMode =
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches);
  const safari = (window.navigator as { standalone?: boolean }).standalone === true;
  return displayMode || safari;
}

/**
 * iPhone and iPad, including the iPads that report themselves as Macs.
 *
 * Since iPadOS 13 a tablet's user agent says "Macintosh", and the only thing
 * that distinguishes it from a desktop is the touch screen - which is what
 * `maxTouchPoints` is doing here rather than any attempt at fingerprinting.
 */
export function isIosDevice(navigatorLike: Navigator = navigator): boolean {
  const ua = navigatorLike.userAgent ?? '';
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  return /Macintosh/.test(ua) && (navigatorLike.maxTouchPoints ?? 0) > 1;
}

/**
 * Safari, and not a Chrome or Firefox wearing its engine.
 *
 * On iOS every browser is Safari underneath, but only Safari itself has the
 * Add to Home Screen item in its share sheet - so telling a shopper in Chrome
 * for iOS to look for it would be sending them somewhere it is not.
 */
export function isIosSafari(navigatorLike: Navigator = navigator): boolean {
  const ua = navigatorLike.userAgent ?? '';
  return isIosDevice(navigatorLike) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
}

/**
 * A computer, where an installed app has its own window a tab can hand over
 * to. On Android and iOS the installed app is not reachable from the browser.
 */
function isDesktop(): boolean {
  if (typeof navigator === 'undefined') return false;
  return !/Android/i.test(navigator.userAgent ?? '') && !isIosDevice();
}

/** Chrome's answer to "is my own app installed?", or false where it cannot say. */
async function hasInstalledApp(): Promise<boolean> {
  const related = (
    navigator as { getInstalledRelatedApps?: () => Promise<unknown[]> }
  ).getInstalledRelatedApps;
  if (typeof related !== 'function') return false;
  try {
    return (await related.call(navigator)).length > 0;
  } catch {
    return false;
  }
}

export interface InstallState {
  /** What this browser can actually do. */
  capability: InstallCapability;
  /** True when the shopper arrived by scanning the merchant's printed code. */
  fromQrCode: boolean;
  /** Chrome's dialog, or `'unavailable'` where there is no dialog to open. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

/**
 * @param openProtocol The scheme the installed app answers to, which is what
 *   makes `open` possible. Without it an installed shop is simply `installed`.
 */
export function useInstallPrompt(openProtocol: string | null = null): InstallState {
  const deferred = useSyncExternalStore(subscribeToPrompt, () => pendingPrompt, () => null);
  // Two different things: this window *is* the app, or the app exists
  // somewhere and this is a browser tab.
  const [standalone] = useState(() => isStandalone());
  const [installed, setInstalled] = useState(false);
  // A shopper who scanned the code in a shop window is *asking* for the app.
  // Everyone else finds the same offer in the footer.
  //
  // Read through the router rather than off `window.location`, because the
  // shopper may have arrived at any page of the shop and then navigated on -
  // and the marker belongs to the arrival, not to the session.
  const fromQrCode = new URLSearchParams(useLocation().search).get('source') === 'qr';

  useEffect(() => {
    // Already listening in a real page; this covers a storefront mounted
    // without `main.tsx`, which is every test.
    listenForInstallPrompt();
    let live = true;
    const onInstalled = () => setInstalled(true);
    window.addEventListener('appinstalled', onInstalled);
    if (!standalone) {
      void hasInstalledApp().then((found) => {
        if (live && found) setInstalled(true);
      });
    }
    return () => {
      live = false;
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [standalone]);

  const install = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // One shot: the handle is spent whichever way the shopper answered.
    setPendingPrompt(null);
    if (outcome === 'accepted') setInstalled(true);
    return outcome;
  }, [deferred]);

  const capability: InstallCapability = standalone
    ? 'installed'
    : installed
      ? openProtocol && isDesktop()
        ? 'open'
        : 'installed'
      : deferred
        ? 'prompt'
        : typeof navigator !== 'undefined' && isIosSafari()
          ? 'ios'
          : 'manual';

  return {
    capability,
    fromQrCode,
    install,
  };
}
